import React from "react";
import { useForm } from "react-hook-form";
import Button from "./Button";
import Icon from "./Icon";
import TextField from "./TextField";
import styles from "./Contact.module.css";

// NOTE: Due to how Netlify handles form submissions, each property
// in the form data must have a corresponding input field in the
// static hidden form at `/index.html`.
interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
}

interface ContactProps {
  ref?: React.RefObject<HTMLElement | null>;
}

const Contact = React.forwardRef<HTMLElement, ContactProps>((_, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>();

  const [status, setStatus] = React.useState<null | "success" | "error">(null);

  const onSubmit = async (data: ContactFormData) => {
    setStatus(null);

    try {
      const body = new URLSearchParams(Object.entries(data));
      body.append("form-name", "contact");
      const response = await fetch("/", {
        method: "POST",
        body,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      if (response.ok) {
        reset();
        setStatus("success");
        // Clear success message after 5 seconds
        setTimeout(() => setStatus(null), 5000);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch {
      setStatus("error");
      // Clear error message after 8 seconds
      setTimeout(() => setStatus(null), 8000);
    }
  };

  return (
    <section
      ref={ref}
      id={`contact_${React.useId()}`}
      className={styles.contact}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>Get In Touch</h2>
          <p className={styles.subtitle}>
            Ready to start your next project? Let’s discuss how we can help
            bring your vision to life.
          </p>
        </div>

        <form
          name="contact"
          method="POST"
          action="/"
          data-netlify="true"
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Netlify Forms requires this hidden input */}
          <input type="hidden" name="form-name" value="contact" />

          <div className={styles.grid}>
            <TextField
              name="name"
              label="Name"
              required
              type="text"
              error={errors.name}
              register={register("name", {
                required: "Name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              })}
            />

            <TextField
              name="email"
              label="Email"
              required
              type="email"
              error={errors.email}
              register={register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />

            <TextField
              name="phone"
              label="Phone"
              type="tel"
              error={errors.phone}
              register={register("phone")}
            />

            <TextField
              name="company"
              label="Company"
              type="text"
              error={errors.company}
              register={register("company")}
            />

            <TextField
              name="message"
              label="Message"
              required
              type="textarea"
              rows={5}
              error={errors.message}
              register={register("message", {
                required: "Message is required",
                minLength: {
                  value: 10,
                  message: "Message must be at least 10 characters",
                },
              })}
            />
          </div>

          <div className={styles.submitGroup}>
            {status === "success" && (
              <div className={styles.successMessage} role="alert">
                <div className={styles.messageContent}>
                  <Icon type="success" />
                  <p>Thank you! Your message has been sent successfully.</p>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className={styles.errorMessage} role="alert">
                <div className={styles.messageContent}>
                  <Icon type="error" />
                  <p>
                    Sorry, there was an error sending your message. Please try
                    again or contact us directly.
                  </p>
                </div>
              </div>
            )}

            <Button type="submit" disabled={isSubmitting} size="large">
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>

            {status === null && (
              <p className={styles.submitHelp}>
                We’ll get back to you as soon as possible.
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
});

Contact.displayName = "Contact";

export default Contact;
